import { useState } from "react";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import toast from "components/ui/toast/Toast";
import { copyToClipboard } from "lib/clipboard";
import { BidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { useUpdateBidReportPublicity } from "lib/hooks/api/bid/bid_reports/useUpdateBidReportPublicity";
import { BidDetails } from "lib/hooks/api/bid/bids/useBid";
import { EventNames, useTracking } from "lib/tracking";
import {
  DeleteBidReportModal,
  RegenerateAnswersModal,
} from "../requirement_summary_report_section/ReportModals";
import { ShareQualificationReportDropdown } from "./ShareQualificationReportDropdown";

type QualificationReportHeaderActionButtonsProps = {
  bidReportId: string;
  bidId: string;
  bidName?: string;
  noticeGuid?: string;
  notice?: BidReport["notice"];
  urlShareSignature?: string;
  hasEditAccess: boolean;
  winThemes: BidDetails["winThemes"];
  onRegenerateBidReport: () => void;
};

export function QualificationReportHeaderActionButtons({
  bidReportId,
  bidId,
  bidName,
  noticeGuid,
  notice,
  urlShareSignature,
  hasEditAccess,
  winThemes,
  onRegenerateBidReport,
}: QualificationReportHeaderActionButtonsProps) {
  const { logEvent } = useTracking();
  const { mutate: updateBidReportPublicity } = useUpdateBidReportPublicity();

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isRegenerateModalOpen, setRegenerateModalOpen] = useState(false);

  const copyUrl = async (signature: string): Promise<boolean> => {
    const shareUrl = `${window.location.origin}/shared/qualification_report/${bidReportId}?sig=${signature}`;
    const copied = await copyToClipboard({ plain: shareUrl });

    if (!copied) {
      toast.error("Failed to copy link to clipboard");
      return false;
    }

    logEvent(EventNames.qualReportLinkCopied, { "Action type": "Link clicked" });
    return true;
  };

  const copyShareableLink = async (): Promise<boolean> => {
    if (urlShareSignature) {
      return copyUrl(urlShareSignature);
    }

    return new Promise((resolve) => {
      updateBidReportPublicity(
        { input: { bidReportId } },
        {
          onSuccess: async (data) => {
            const signature = data.updateBidReportPublicity.publicHash;
            if (!signature) {
              toast.error("Failed to copy shareable link");
              resolve(false);
              return;
            }
            resolve(await copyUrl(signature));
          },
          onError: () => {
            toast.error("Failed to copy shareable link");
            resolve(false);
          },
        },
      );
    });
  };

  return (
    <>
      <Flex gap={8}>
        <ShareQualificationReportDropdown
          bidId={bidId}
          bidName={bidName}
          bidReportId={bidReportId}
          noticeGuid={noticeGuid}
          notice={notice}
          winThemes={winThemes}
          onCopyLink={copyShareableLink}
        />
        {hasEditAccess && (
          <>
            <DropdownMenu
              trigger={
                <Button
                  buttonType="tertiary"
                  icon="dotsVertical"
                  iconProps={{ color: "sysTextDefault" }}
                />
              }
              width={175}
            >
              <DropdownMenuItem
                key="regenerate"
                icon="refreshCw03"
                onClick={() => setRegenerateModalOpen(true)}
              >
                Regenerate
              </DropdownMenuItem>
              <DropdownMenuItem
                key="delete"
                icon="trash03"
                iconProps={{ color: "sysDestructiveDefault" }}
                onClick={() => setDeleteModalOpen(true)}
              >
                <Text color="sysDestructiveDefault">Delete</Text>
              </DropdownMenuItem>
            </DropdownMenu>
            <DeleteBidReportModal
              bidReportId={bidReportId}
              bidId={bidId}
              isOpen={isDeleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
            />
            <RegenerateAnswersModal
              isOpen={isRegenerateModalOpen}
              onRegenerate={onRegenerateBidReport}
              onClose={() => setRegenerateModalOpen(false)}
              modalText="You're about to regenerate all AI responses in this report. Please note, this action cannot be undone."
            />
          </>
        )}
      </Flex>
    </>
  );
}
